import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Search, UserPlus, Mail, X } from "lucide-react";
import { usePagination } from "@/shared/hooks/usePagination";
import { DataPagination } from "@/shared/components/DataPagination";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { ProfileService } from "@/features/Profile/services/profileService";
import { UserProfile } from "@/features/Profile/types/profileTypes";
import { InviteUserDialog } from "@/features/Users/components/InviteUserDialog";
import { WorkspaceService } from "@/features/Workspaces/services/workspaceService";
import { useOrganization } from "@/shared/hooks/api/useOrganization";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { InviteService } from "@/features/Users/services/inviteService";
import { Badge } from "@/shared/components/ui/badge";
import { useToast } from "@/shared/hooks/use-toast";
import { logger } from "@/infrastructure/lib/logger";

// Using the UserProfile interface from ProfileService

interface ProductUsersFormProps {
  onSelectedUsersChange: (userIds: string[]) => void;
  initialSelectedUsers?: string[];
  currentWorkspaceId?: string; // ID do produto sendo editado (undefined = criação)
  savedWorkspaceId?: string; // ID do produto recém-criado (após salvar aba General)
}

export const WorkspaceUsersForm = ({
  onSelectedUsersChange,
  initialSelectedUsers = [],
  currentWorkspaceId,
  savedWorkspaceId
}: ProductUsersFormProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedUsers, setSelectedUsers] = useState<string[]>(initialSelectedUsers);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const { data: userOrganization } = useOrganization();
  const { selectedOrganization } = useSuperAdminOrg();
  // Entra na queryKey abaixo: sem o id da organização ativa, o cache da
  // organização anterior sobreviveria à troca de organização.
  const effectiveOrgId = selectedOrganization?.id ?? userOrganization?.organization_id;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { 
    currentPage, 
    pageSize, 
    setCurrentPage, 
    setPageSize, 
    paginateData 
  } = usePagination({
    initialPage: 1,
    initialPageSize: 5
  });

  const {
    currentPage: invitesPage,
    pageSize: invitesPageSize,
    setCurrentPage: setInvitesPage,
    setPageSize: setInvitesPageSize,
    paginateData: paginateInvites
  } = usePagination({
    initialPage: 1,
    initialPageSize: 5
  });

  // Mutation para cancelar convite
  const { mutate: cancelInvite, isPending: isCancelingInvite } = useMutation({
    mutationFn: async (inviteId: string) => {
      await InviteService.deleteInvite(inviteId);
    },
    onSuccess: () => {
      // Invalidar queries para atualizar a lista
      queryClient.invalidateQueries({ queryKey: ['workspace-pending-invites'] });
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      
      toast({
        title: t("settings.users.inviteCancelSuccess"),
        description: t("settings.users.inviteCancelSuccessDescription"),
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: t("settings.users.inviteCancelError"),
        description: t("settings.users.inviteCancelErrorDescription"),
      });
    },
  });

  // Inicializar selectedUsers apenas na montagem do componente
  useEffect(() => {
    // Apenas setar na inicialização se tiver valores
    if (initialSelectedUsers && initialSelectedUsers.length > 0 && selectedUsers.length === 0) {
      setSelectedUsers(initialSelectedUsers);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar APENAS na montagem
  
  // Sincronizar quando initialSelectedUsers muda (mas não resetar para vazio)
  useEffect(() => {
    if (initialSelectedUsers && initialSelectedUsers.length > 0) {
      setSelectedUsers(initialSelectedUsers);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedUsers.join(',')]); // Usar join para evitar loops infinitos

  // ID do produto para buscar convites (usa savedWorkspaceId se disponível)
  const workspaceIdForInvites = savedWorkspaceId || currentWorkspaceId;

  // Fetch organization users using JWT token via ProfileService
  const { data: organizationUsers = [], isLoading, error } = useQuery<UserProfile[]>({
    queryKey: ['organization-users', effectiveOrgId],
    queryFn: async () => {
      try {
        // Usar o ProfileService para obter usuários da organização atual
        const users = await ProfileService.getCurrentOrganizationUsers();
        
        if (!users) {
          return [];
        }
        
        // Somente ativos. Superadmins da org aparecem (decisão 2026-07-02):
        // o modelo permite superadmin como membro de workspace (product_users).
        const activeUsers = users.filter(u => u.status === 'active');

        return activeUsers;
      } catch (error) {
        logger.error('Failed to fetch organization users', { error });
        throw error;
      }
    },
    staleTime: 60000, // 1 minuto
    retry: 1,
    refetchOnWindowFocus: false,
    enabled: !!user?.id
  });

  // Fetch organization products for invite dialog
  const { data: organizationProducts = [] } = useQuery({
    queryKey: ['organization-products', effectiveOrgId],
    queryFn: async () => {
      try {
        const products = await WorkspaceService.getCurrentOrganizationProducts();
        return products;
      } catch (error) {
        logger.error('Failed to fetch organization products', { error });
        return [];
      }
    },
    staleTime: 60000,
    enabled: !!user?.id
  });

  // Fetch pending invites for this workspace
  const { data: pendingInvites = [] } = useQuery({
    queryKey: ['workspace-pending-invites', workspaceIdForInvites],
    queryFn: async () => {
      if (!workspaceIdForInvites) {
        return [];
      }
      
      try {
        const allInvites = await InviteService.listInvites();
        
        // Filtrar convites que incluem este produto
        const productInvites = allInvites.filter(invite => {
          const productIds = invite.product_ids || [];
          return productIds.includes(workspaceIdForInvites);
        });
        return productInvites;
      } catch (error) {
        logger.error('Failed to fetch workspace invites', { error });
        return [];
      }
    },
    enabled: !!workspaceIdForInvites,
    staleTime: 0, // Desabilitar cache
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });


  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    return organizationUsers.filter((orgUser) => {
      if (!searchQuery.trim()) return true;
      
      const searchLower = searchQuery.toLowerCase().trim();
      const nameMatch = orgUser.full_name?.toLowerCase().includes(searchLower) || false;
      const emailMatch = orgUser.email.toLowerCase().includes(searchLower);
      
      return nameMatch || emailMatch;
    });
  }, [organizationUsers, searchQuery]);

  const paginatedUsers = useMemo(() => {
    return paginateData(filteredUsers);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsers, currentPage, pageSize]);

  const paginatedInvites = useMemo(() => {
    return paginateInvites(pendingInvites);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInvites, invitesPage, invitesPageSize]);

  useEffect(() => {
    setCurrentPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Handle user selection toggling
  const handleUserToggle = (userId: string, checked: boolean) => {
    const newSelected = checked 
      ? [...selectedUsers, userId]
      : selectedUsers.filter(id => id !== userId);
    
    setSelectedUsers(newSelected);
    onSelectedUsersChange(newSelected);
  };

  // Exibir mensagem de carregamento ou erro
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-destructive">{t("common.errorLoadingData")}</p>
        <pre className="text-xs mt-2 text-muted-foreground">
          {resolveApiErrorMessage(error, t)}
        </pre>
      </div>
    );
  }

  // Se há 0 ou apenas 1 usuário E é um produto NOVO (não edição), mostrar apenas o CTA
  // Na EDIÇÃO, a lista deve SEMPRE aparecer
  const isNewProduct = !currentWorkspaceId;
  
  if (organizationUsers.length <= 1 && isNewProduct) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        {/* Call to Action - Convidar Usuário */}
        <div className="flex flex-col items-center justify-center p-6 border border-dashed rounded-md bg-muted/30 max-w-md mb-4">
          <p className="text-sm text-muted-foreground text-center mb-3">
            {t("settings.workspaces.inviteUserCta")}
          </p>
          <Button
            onClick={() => setInviteDialogOpen(true)}
            className="flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            {t("settings.users.inviteButton")}
          </Button>
        </div>

        {/* Convites Enviados */}
        {pendingInvites.length > 0 && (
          <div className="space-y-2 w-full max-w-md">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">{t("settings.workspaces.pendingInvites")}</h3>
              <Badge variant="secondary">{pendingInvites.length}</Badge>
            </div>
            <div className="border rounded-md p-3 bg-muted/30 space-y-3">
              {paginatedInvites.map((invite, index) => (
                <div key={invite.id}>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 flex-1">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">
                          {invite.user_name?.charAt(0).toUpperCase() || invite.email.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium">{invite.user_name}</p>
                        <p className="text-xs text-muted-foreground">{invite.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {t("settings.users.awaitingAccess")}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => cancelInvite(invite.id)}
                        disabled={isCancelingInvite}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {index < paginatedInvites.length - 1 && (
                    <div className="border-t mt-3" />
                  )}
                </div>
              ))}
            </div>

            {pendingInvites.length > 5 && (
              <DataPagination
                currentPage={invitesPage}
                pageSize={invitesPageSize}
                totalItems={pendingInvites.length}
                onPageChange={setInvitesPage}
                onPageSizeChange={setInvitesPageSize}
                pageSizeOptions={[5, 10, 20]}
                showInfo={false}
              />
            )}
          </div>
        )}

        {/* Dialog de Convite */}
        <InviteUserDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          products={organizationProducts}
          organizationId={effectiveOrgId}
          preselectedProductId={workspaceIdForInvites}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Call to Action - Convidar Usuário */}
      <div className="flex flex-col items-center justify-center p-4 border border-dashed rounded-md bg-muted/30">
        <p className="text-sm text-muted-foreground text-center mb-3">
          {t("settings.workspaces.inviteUserCta")}
        </p>
        <Button
          onClick={() => setInviteDialogOpen(true)}
          className="flex items-center gap-2"
          variant="outline"
        >
          <UserPlus className="h-4 w-4" />
          {t("settings.users.inviteButton")}
        </Button>
      </div>

      {/* Convites Enviados */}
      {pendingInvites.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">{t("settings.workspaces.pendingInvites")}</h3>
            <Badge variant="secondary">{pendingInvites.length}</Badge>
          </div>
          <div className="border rounded-md p-3 bg-muted/30 space-y-3">
            {paginatedInvites.map((invite, index) => (
              <div key={invite.id}>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 flex-1">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs">
                        {invite.user_name?.charAt(0).toUpperCase() || invite.email.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium">{invite.user_name}</p>
                      <p className="text-xs text-muted-foreground">{invite.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {t("settings.users.awaitingAccess")}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => cancelInvite(invite.id)}
                      disabled={isCancelingInvite}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {index < paginatedInvites.length - 1 && (
                  <div className="border-t mt-3" />
                )}
              </div>
            ))}
          </div>

          {pendingInvites.length > 5 && (
            <DataPagination
              currentPage={invitesPage}
              pageSize={invitesPageSize}
              totalItems={pendingInvites.length}
              onPageChange={setInvitesPage}
              onPageSizeChange={setInvitesPageSize}
              pageSizeOptions={[5, 10, 20]}
              showInfo={false}
            />
          )}
        </div>
      )}

      {/* Barra de pesquisa */}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={t("settings.workspaces.searchUsers")}
          className="pl-8"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Resultados da pesquisa */}
      {filteredUsers.length === 0 ? (
        <div className="p-4 text-center border rounded-md">
          <p className="text-muted-foreground">{t("settings.workspaces.noUsersFound")}</p>
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>{t("settings.workspaces.fields.userName")}</TableHead>
                  <TableHead className="w-[100px]">{t("settings.workspaces.fields.access")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.map((orgUser) => (
                <TableRow key={orgUser.id}>
                  <TableCell>
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={orgUser.avatar_url || ''}
                        alt={orgUser.full_name || orgUser.email}
                      />
                      <AvatarFallback>
                        {getInitials(orgUser.full_name || orgUser.email)}
                      </AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{orgUser.full_name || t("settings.workspaces.noName")}</span>
                        {orgUser.id === user?.id && (
                          <span className="text-xs text-muted-foreground">{t("settings.workspaces.meLabel")}</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{orgUser.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      id={`user-${orgUser.id}`}
                      checked={selectedUsers.includes(orgUser.id)}
                      onCheckedChange={(checked) =>
                        handleUserToggle(orgUser.id, checked as boolean)
                      }
                    />
                  </TableCell>
                </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <DataPagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={filteredUsers.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[5, 10, 20, 50]}
          />
        </>
      )}

      {/* Dialog de Convite */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        products={organizationProducts}
        organizationId={effectiveOrgId}
        preselectedProductId={currentWorkspaceId}
      />
    </div>
  );
};
